import { Card } from '@ksyos/design-system';
import { flatten, set } from 'lodash';
import type { IService } from '@common/interfaces';
import type { IEpisodeCategoryBasic } from '../../../../../common/interfaces/episodeCategory';
import type {
    IServiceGroupWithProfileSettings,
    IServiceGroupWithServices,
    IServiceOrganizationSettings,
    IServicesByServiceGroupForEpisodeCategory,
} from '../../../../../common/interfaces/servicesByServiceGroupForEpisodeCategory';
import type { ICareProviderAddCarePathState } from '../../../interfaces/careProviderAddCarePathState';
import type { IEmployment } from '../../../interfaces/employment';
import type { IOrganization } from '../../../interfaces/organization';
import type { IPractitioner } from '../../../interfaces/practitioner';
import type { IPractitionerEmployment } from '../../carePath/steps/ChooseProfession';
import { ServiceCode } from '../../../../../common/enums/serviceCode';
import { translateKey } from '../../../../translate';
import { useAbortableFetch } from '../../../../utils/hooks/useAbortableFetch';
import { initializeServiceOrganizationSettings } from '../../../../utils/initializeServiceOrganizationSettings';
import { ChooseCarePathStep } from '../../../interfaces/careProviderAddCarePathState';
import { PreferredServiceOrganizationConsumers } from '../../carePath/settings/components/PreferredServiceOrganizationConsumers';
import { ChooseEpisodeCategory } from '../../carePath/steps/ChooseEpisodeCategory';
import { ChooseProfession } from '../../carePath/steps/ChooseProfession';
import { ChooseRoles } from '../../carePath/steps/ChooseRoles';
import { ChooseServiceGroups } from '../../carePath/steps/ChooseServiceGroups';

interface IChooseCarePathProps {
    addCarePathState: ICareProviderAddCarePathState;
    employment?: IEmployment;
    isProfessionSelectionNeeded: boolean;
    organization?: IOrganization;
    practitioner?: IPractitioner;
    serviceGroupMembershipsGroupedByEpisodeCategories: IServicesByServiceGroupForEpisodeCategory[];
    serviceIdsNotContainingPreferredServiceOrganizationConsumers: string[];
    showRegistrationCodesError?: boolean;
    updateState: (state: Partial<ICareProviderAddCarePathState>, carePathStep: ChooseCarePathStep) => void;
}

export function ChooseCarePath(props: IChooseCarePathProps) {
    const abortableFetch = useAbortableFetch();

    return (
        <>
            {renderEpisodeCategory()}
            {renderProfession()}
            {renderServiceGroups()}
            {renderRoles()}
            {renderAddPreferredServiceConsumers()}
        </>
    );

    function renderEpisodeCategory() {
        if (props.addCarePathState.preSelectedEpisodeCategoryName) {
            return null;
        }

        return (
            <ChooseEpisodeCategory
                episodeCategory={props.addCarePathState.episodeCategory}
                serviceGroupMembershipsGroupedByEpisodeCategories={
                    props.serviceGroupMembershipsGroupedByEpisodeCategories
                }
                updateState={updateEpisodeCategory}
            />
        );
    }

    function renderProfession() {
        if (
            props.employment &&
            props.practitioner &&
            (props.addCarePathState.carePathStep === ChooseCarePathStep.chooseProfession ||
                props.isProfessionSelectionNeeded)
        ) {
            return (
                <ChooseProfession
                    onChangePractitionerEmployment={(value) => updatePractitionerEmployment(value)}
                    originalPractitionerEmployment={{
                        employment: props.employment,
                        practitioner: props.practitioner,
                    }}
                    practitionerEmploymentState={{
                        employment: props.addCarePathState.employment!,
                        practitioner: props.addCarePathState.practitioner!,
                    }}
                    showRegistrationCodesError={props.showRegistrationCodesError}
                />
            );
        }

        return null;
    }

    function renderServiceGroups() {
        if (
            props.addCarePathState.episodeCategory &&
            (props.addCarePathState.carePathStep === ChooseCarePathStep.addServiceGroups ||
                props.addCarePathState.serviceGroups)
        ) {
            return (
                <ChooseServiceGroups
                    episodeCategory={props.addCarePathState.episodeCategory}
                    serviceGroupMembershipsGroupedByEpisodeCategories={
                        props.serviceGroupMembershipsGroupedByEpisodeCategories
                    }
                    serviceGroups={props.addCarePathState.serviceGroups}
                    updateState={updateServiceGroups}
                />
            );
        }

        return null;
    }

    function renderRoles() {
        return (props.addCarePathState.serviceGroups ?? []).map((serviceGroup, index) => (
            <ChooseRoles
                employmentStartDate={props.addCarePathState?.employment?.startDate}
                episodeCategory={props.addCarePathState.episodeCategory!}
                key={serviceGroup.id}
                sequence={index + 2}
                serviceGroup={serviceGroup}
                updateState={updateRoles}
            />
        ));
    }

    function updatePreferredServiceConsumers(preferredServiceConsumers: string[], serviceId: string) {
        const serviceGroups = props.addCarePathState.serviceGroups!.map((servicegroup) => {
            if ((servicegroup.services ?? []).find((service) => service.id === serviceId)) {
                set(servicegroup, `preferredServiceOrganizationConsumers.${serviceId}`, preferredServiceConsumers);
            }

            return servicegroup;
        });

        props.updateState({ serviceGroups }, ChooseCarePathStep.addPreferredServiceConsumers);
    }

    function renderAddPreferredServiceConsumers() {
        const preferredServiceOrganizationConsumers = (serviceId: string) => {
            const serviceGroup = (props.addCarePathState.serviceGroups ?? []).find(
                (sg) => sg.preferredServiceOrganizationConsumers?.[serviceId]
            );

            return serviceGroup?.preferredServiceOrganizationConsumers?.[serviceId] ?? [];
        };

        return (props.addCarePathState.serviceGroups ?? [])
            .filter(
                (serviceGroup) =>
                    serviceGroup.profileSettings && serviceGroup.profileSettings.serviceProviderSettings.length > 0
            )
            .map((serviceGroup) => serviceGroup.profileSettings!.serviceProviderSettings)
            .reduce((acc, val) => acc.concat(val), [])
            .filter(
                (serviceProviderSetting) =>
                    serviceProviderSetting.isServiceConsumerSelector && !serviceProviderSetting.isDisabled
            )
            .map((serviceProvider) => (
                <Card key={`preferredServiceConsumersFor${serviceProvider.id}`}>
                    <Card.Body>
                        <PreferredServiceOrganizationConsumers
                            hasError={props.serviceIdsNotContainingPreferredServiceOrganizationConsumers.includes(
                                serviceProvider.service.id
                            )}
                            onChange={(preferredServiceConsumers) =>
                                updatePreferredServiceConsumers(preferredServiceConsumers, serviceProvider.service.id)
                            }
                            onDoValidate={() => {
                                return;
                            }}
                            selectedConsumerOrganizationIds={preferredServiceOrganizationConsumers(
                                serviceProvider.service.id
                            )}
                            service={serviceProvider.service}
                            title={`${translateKey('carePath.requesterPreferenceList')} ${serviceProvider.service.name}`}
                        />
                    </Card.Body>
                </Card>
            ));
    }

    function updateEpisodeCategory(episodeCategory?: IEpisodeCategoryBasic) {
        props.updateState({ episodeCategory }, ChooseCarePathStep.chooseEpisodeCategory);
    }

    function updatePractitionerEmployment(practitionerEmployment: IPractitionerEmployment) {
        props.updateState(practitionerEmployment, ChooseCarePathStep.chooseProfession);
    }

    function updateServiceGroups(serviceGroups?: IServiceGroupWithProfileSettings[]) {
        props.updateState({ serviceGroups }, ChooseCarePathStep.addServiceGroups);
    }

    async function updateRoles(serviceGroupWithRole: IServiceGroupWithServices) {
        let missingLinkedServiceIds: string[] = [];

        if (props.organization) {
            const serviceOrganizationSettings: IServiceOrganizationSettings[] = (
                await abortableFetch.fetchJson(
                    `/api/serviceOrganizationSettings?organizationId=${props.organization.id}&isActive=true`,
                    'GET'
                )
            ).json.items;

            if (
                serviceGroupWithRole.profile &&
                serviceGroupWithRole.profile.id &&
                serviceGroupWithRole.services &&
                serviceGroupWithRole.profileSettings
            ) {
                missingLinkedServiceIds = serviceGroupWithRole.services
                    .map((service) => service.id)
                    .filter(
                        (serviceId) => !serviceOrganizationSettings.find((setting) => setting.serviceId === serviceId)
                    )
                    .filter((serviceId) =>
                        flatten<{ isDisabled: boolean; serviceId: string }>(
                            Object.values(serviceGroupWithRole.profileSettings!)
                        ).some((setting) => setting.serviceId === serviceId && !setting.isDisabled)
                    );
            }
        }

        const serviceGroups = props.addCarePathState.serviceGroups!.map((serviceGroup) => {
            if (serviceGroup.id === serviceGroupWithRole.id) {
                const servicesToAddSettingsFor = (serviceGroupWithRole.services ?? []).filter((service) =>
                    missingLinkedServiceIds.includes(service.id)
                );

                return {
                    ...serviceGroupWithRole,
                    serviceOrganizationSettings: servicesToAddSettingsFor.map((service) => {
                        const settings = initializeServiceOrganizationSettings(service);

                        if (isTooAndIsProviderRoleAssigned(service, serviceGroupWithRole)) {
                            settings.isRequestRecipient = true;
                        }

                        return settings;
                    }),
                };
            }

            return serviceGroup;
        });

        props.updateState({ serviceGroups }, ChooseCarePathStep.addRoles);
    }

    function isTooAndIsProviderRoleAssigned(
        service: IService,
        serviceGroupWithRole: IServiceGroupWithServices
    ): boolean {
        return Boolean(
            service.code === ServiceCode.TOO &&
                serviceGroupWithRole.profileSettings?.serviceProviderSettings?.some(
                    (o) => o.service.code === ServiceCode.TOO
                )
        );
    }
}
